/* Conversa com o Jarvis (Etapa 2). Estado compartilhado entre a tela inicial e a
   tela Conversa. A IA roda no servidor; aqui só chegam o texto e os números de consumo. */
import { useSyncExternalStore } from 'react';
import type { AiStatus, ApiErrorBody, ChatMessage, MemoryRecord, Source } from '@jarvis/shared';
import { CLIENT_HEADER } from '@jarvis/shared/constants';
import { api, ApiError } from './api';

export interface Suggestion { key: number; text: string; category: string; state: 'new' | 'saving' | 'saved' }

export interface ChatState {
  status: AiStatus | null;
  statusError: string;
  messages: ChatMessage[];
  historyLoaded: boolean;
  streaming: boolean;
  /* Resposta chegando agora, ainda incompleta. */
  draft: string;
  /* Pergunta em andamento, mostrada antes de o servidor confirmar. */
  pendingQuestion: string;
  suggestions: Suggestion[];
  error: { code: string; message: string } | null;
  /* Busca na web em andamento (a pesquisa feita) e fontes citadas até agora. */
  searching: string;
  liveSources: Source[];
  notice: string;
  /* O que o Jarvis acabou de guardar sozinho (com opção de desfazer). */
  learned: (MemoryRecord & { undone?: boolean })[];
}

let state: ChatState = {
  status: null, statusError: '', messages: [], historyLoaded: false, streaming: false,
  draft: '', pendingQuestion: '', suggestions: [], error: null, searching: '', liveSources: [], notice: '', learned: []
};
const listeners = new Set<() => void>();
const set = (patch: Partial<ChatState>) => { state = { ...state, ...patch }; listeners.forEach((l) => l()); };
let seq = 0;
let controller: AbortController | null = null;

export const chat = {
  get: () => state,
  subscribe(l: () => void) { listeners.add(l); return () => { listeners.delete(l); }; },

  async loadStatus() {
    try { set({ status: await api.get<AiStatus>('/api/ai/status'), statusError: '' }); }
    catch (e) { set({ statusError: e instanceof Error ? e.message : 'Falha ao consultar a IA.' }); }
  },

  async loadHistory() {
    try {
      const r = await api.get<{ items: ChatMessage[] }>('/api/chat');
      set({ messages: r.items, historyLoaded: true });
    } catch { set({ historyLoaded: true }); }
  },

  /* Envia uma mensagem e acompanha a resposta em pedaços. Devolve o texto final. */
  async send(message: string, name?: string, opts: { voice?: boolean; onDelta?: (t: string) => void } = {}): Promise<string> {
    const q = message.trim();
    if (!q || state.streaming) return '';
    controller = new AbortController();
    set({ streaming: true, draft: '', pendingQuestion: q, error: null, searching: '', liveSources: [], notice: '' });
    let text = '';
    let sources: Source[] = [];
    try {
      const res = await fetch('/api/ai/chat', {
        method: 'POST', credentials: 'include', signal: controller.signal,
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', [CLIENT_HEADER]: 'web' },
        body: JSON.stringify({ message: q, name: name || undefined, voice: opts.voice || undefined })
      });
      if (!res.ok || !res.body) {
        const body = (await res.json().catch(() => null)) as ApiErrorBody | null;
        if (res.status === 401) window.dispatchEvent(new Event('jm:auth-required'));
        throw new ApiError(body?.error?.message ?? `O servidor respondeu com erro ${res.status}.`, body?.error?.code ?? `HTTP_${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let cut: number;
        while ((cut = buf.indexOf('\n\n')) >= 0) {
          const block = buf.slice(0, cut);
          buf = buf.slice(cut + 2);
          const ev = /^event: ?(.*)$/m.exec(block)?.[1]?.trim();
          const raw = block.split('\n').filter((l) => l.startsWith('data:')).map((l) => l.slice(5).trimStart()).join('\n');
          if (!ev || !raw) continue;
          const data = JSON.parse(raw);
          if (ev === 'delta') { text += data.t; set({ draft: text, searching: '' }); opts.onDelta?.(data.t); }
          else if (ev === 'learned') set({ learned: [...state.learned, data as MemoryRecord] });
          else if (ev === 'searching') set({ searching: data.query || 'pesquisando' });
          else if (ev === 'source') set({ liveSources: [...state.liveSources, data as Source] });
          else if (ev === 'notice') set({ notice: data.message });
          else if (ev === 'memory') set({ suggestions: [...state.suggestions, { key: ++seq, text: data.text, category: data.category, state: 'new' }] });
          else if (ev === 'done') { sources = data.sources ?? []; set({ status: data.status }); }
          else if (ev === 'error') set({ error: { code: data.code, message: data.message } });
        }
      }
    } catch (e) {
      if (!(e instanceof DOMException && e.name === 'AbortError')) {
        set({ error: e instanceof ApiError ? { code: e.code, message: e.message } : { code: 'FALHA', message: 'Não foi possível falar com o servidor do Jarvis.' } });
      }
    } finally {
      controller = null;
      const now = new Date().toISOString();
      const added: ChatMessage[] = [{ id: `local-q-${++seq}`, role: 'user', content: q, createdAt: now }];
      if (text.trim()) added.push({ id: `local-a-${++seq}`, role: 'assistant', content: text, sources: sources.length ? sources : state.liveSources, createdAt: now });
      set({ messages: [...state.messages, ...added], streaming: false, draft: '', pendingQuestion: '', searching: '', liveSources: [] });
      if (!state.status || state.error) void chat.loadStatus();
    }
    return text;
  },

  stop() { controller?.abort(); },

  async clear() {
    await api.del('/api/chat');
    set({ messages: [], suggestions: [], error: null });
  },

  async accept(key: number) {
    const s = state.suggestions.find((x) => x.key === key);
    if (!s || s.state !== 'new') return;
    const mark = (st: Suggestion['state']) => set({ suggestions: state.suggestions.map((x) => (x.key === key ? { ...x, state: st } : x)) });
    mark('saving');
    try {
      await api.post<{ item: MemoryRecord }>('/api/memories', { text: s.text, category: s.category, source: 'assistant' });
      mark('saved');
    } catch (e) {
      mark('new');
      throw e;
    }
  },

  /* Desfaz algo que o Jarvis guardou sozinho: apaga da memória. */
  async undoLearned(id: string) {
    await api.del(`/api/memories/${id}`);
    set({ learned: state.learned.map((m) => (m.id === id ? { ...m, undone: true } : m)) });
  },

  dismiss(key: number) { set({ suggestions: state.suggestions.filter((x) => x.key !== key) }); }
};

export function useChat(): ChatState {
  return useSyncExternalStore(chat.subscribe, chat.get);
}

/* Texto para falar em voz alta: sem marcações de formatação. */
export function forSpeech(text: string): string {
  return text.replace(/```[\s\S]*?```/g, ' ').replace(/[*_#`>]+/g, '').replace(/\[(.+?)\]\(.+?\)/g, '$1').trim();
}
